import React from "react";
import { Alert, AlertTitle, Box, Chip, Stack, Typography } from "@mui/material";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import { useWeatherAnomalies } from "../hooks/useMetrics";
import type { WeatherAnomaly } from "../api/weather";
import type { MetricsFilters } from "../api/types";

interface WeatherAnomalyCalloutProps {
  filters: MetricsFilters;
  enabled?: boolean;
}

const severityToAlert = (
  anomalies: WeatherAnomaly[],
): "error" | "warning" | "info" => {
  if (anomalies.some((a) => a.severity === "severe")) return "error";
  if (anomalies.some((a) => a.severity === "warning")) return "warning";
  return "info";
};

const formatRange = (a: WeatherAnomaly): string =>
  a.startDay === a.endDay ? a.startDay : `${a.startDay} → ${a.endDay}`;

/**
 * Surfaces abnormal-weather callouts (heatwaves, cold snaps, heavy rain) for the
 * current cohort/filter, including forward-looking forecast warnings. Renders
 * nothing when there are no anomalies in the window.
 */
export const WeatherAnomalyCallout: React.FC<WeatherAnomalyCalloutProps> = ({
  filters,
  enabled = true,
}) => {
  const { data } = useWeatherAnomalies({ ...filters, enabled });
  const anomalies = data?.data.anomalies ?? [];

  if (!anomalies.length) return null;

  const observed = anomalies.filter((a) => !a.isForecast);
  const forecast = anomalies.filter((a) => a.isForecast);

  return (
    <Alert severity={severityToAlert(anomalies)} icon={<WbSunnyIcon />}>
      <AlertTitle sx={{ fontWeight: 700 }}>
        Abnormal weather in this period
      </AlertTitle>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Weather extremes can affect recovery, sleep and strain. Consider these
        when reviewing the metrics below.
      </Typography>

      <Stack spacing={0.75}>
        {observed.map((a, i) => (
          <Box key={`obs-${i}`}>
            <Chip size="small" label={formatRange(a)} sx={{ mr: 1, height: 20 }} />
            <Typography variant="body2" component="span">
              {a.label}
            </Typography>
          </Box>
        ))}
        {forecast.map((a, i) => (
          <Box key={`fcst-${i}`}>
            <Chip
              size="small"
              color="warning"
              label={`Forecast · ${formatRange(a)}`}
              sx={{ mr: 1, height: 20 }}
            />
            <Typography variant="body2" component="span" fontWeight={600}>
              {a.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Alert>
  );
};
